/**
 * Whether a deleted-task Restore control should be offered, and why not.
 *
 * The rule is the backend's (`app/services/task_restore.py`): a deleted task
 * may be restored exactly when the same task could be created now. Only the
 * episode-status half is mirrored here, and it gates visits alone, because only
 * creating a visit requires an episode that accepts visits (active or
 * completed — EMR-3341). An order's rules (its one-per-episode slot, its linked
 * visit) depend on state this page does not have, so they stay server-side and
 * surface as a refusal toast. This copy exists so the control can explain
 * itself before the request rather than offering an action that always fails;
 * the backend remains the authority and refuses the same case for the same
 * reason, in its own wording.
 *
 * The status is a bare string on the wire (`TaskListResponse.episode_status`),
 * so there is no generated enum to key off; the literals below match
 * EpisodeStatus values the backend serialises.
 *
 * Date-closed periods (EMR-1859) are not mirrored here: the row does not always
 * carry enough to decide, and a holder of `episode:amend-closed` must still be
 * able to offer restore. The write path refuses with 403 when needed.
 */

import { VISIT_ACCEPTING_EPISODE_STATUSES } from '../patient/patientDetailTaskConstants';

export const RESTORE_BLOCKED_BY_EPISODE =
  'This episode cannot accept visits, so this visit cannot be restored.';

export interface RestorableTask {
  task_type: string;
  episode_status?: string | null;
}

/**
 * The reason this task cannot be restored, or null when it can be.
 *
 * A missing `episode_status` counts as blocked: the control is disabled with
 * a reason rather than offered on an answer we do not have.
 */
export function taskRestoreBlockedReason(task: RestorableTask): string | null {
  if (task.task_type !== 'visit') return null;
  return task.episode_status &&
    VISIT_ACCEPTING_EPISODE_STATUSES.has(task.episode_status)
    ? null
    : RESTORE_BLOCKED_BY_EPISODE;
}
